import { createElement, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { EyeBlueIcon } from "../../../../../../assets";
import {
  OrganisationTypeLabels,
  ProjectAccessConstants,
  ProjectPermissionConstants,
} from "../../../../../../constants";
import {
  AccessTableRow,
  CursorChangeProps,
  InviteTableRow,
  NavigationSearchParams,
  ResultType,
} from "../../../../../../models";
import { changeProjectResponsibleOwner } from "../../../../../../service/organisation";
import {
  SearchProjectOrganisationInvitations,
  SearchProjectOrganisationInvitationsRequest,
  SearchProjectOrganisationInvitationsResponse,
  searchProjectOrganisations,
} from "../../../../../../service/query";
import { FilterGroups, ResultData, Status } from "../../../../../../service/Shared";
import { getErrorMessageFromCode } from "../../../../../../service/ValidationErrorFormatter";
import { useAuth } from "../../../../../../useAuth";
import { flattenObject, useIsLoadingWrapper } from "../../../../../../utils";
import { getProjectAccessDetailsRoute, getProjectAccessInviteAcceptRoute } from "../../../../../../utils/routes";
import { DataGridIconCellFormatterData, dataGridMapFilterCriteria, Toast } from "../../../../../../widget";
import { ProjectContext } from "../../ProjectContext";

type InviteSortKey = NonNullable<SearchProjectOrganisationInvitationsRequest["sort"]>[number]["key"];

/**
 * The Invites grid runs server-side, so CursorDataGrid ignores ProjectInvitesTable's own row
 * mapping and renders whatever onChange returns. Its column keys are the widget's, but the GraphQL
 * keys are the read model's, so both directions are translated here. Filters nest under
 * `filter.results`, so they stay unprefixed; sort keys carry the `results.` prefix.
 */
// organisationName is absent: its filter is applied per branch in onChange, as the column shows the invited
// organisation for what this organisation sent but the requester for a request sent to it.
const INVITE_COLUMN_TO_FILTER_KEY: Record<string, string> = {
  access: "role",
  dateSent: "sentDate",
  createdBy: "sentByUser.fullName",
  status: "calculatedStatus",
};

// The query wrapper returns `errors: []` for any failure without networkError.result.errors - a
// dropped connection, a gateway 502, a graphQLErrors-only rejection - so there is often no code to
// look up and the failure still has to be reported.
const ACCESS_ROWS_ERROR_MESSAGE =
  "Couldn't load the organisations with access to this project - please try again later.";

const INVITE_COLUMN_TO_SORT_KEY: Record<string, InviteSortKey> = {
  organisationName: "results.targetOrganisation.displayName",
  access: "results.role",
  dateSent: "results.sentDate",
  createdBy: "results.sentByUser.fullName",
  organisationType: "results.organisationType",
};

interface UseAccessTabReturnData {
  accessRows: AccessTableRow[];
  accessIsLoading: boolean;
  hasAccessError: boolean;
  onResponsibleOwnerChange: (organisationUuid: string) => void;
  onEditAccess: () => void;
  onSaveAccessEdit: () => void;
  onCancelAccessEdit: () => void;
  showAssignResponsibleOwnerModal: boolean;
  newResponsibleOwnerName?: string;
  currentResponsibleOwnerName?: string;
  onConfirmAssignResponsibleOwner: () => void;
  onCancelAssignResponsibleOwner: () => void;
  isEditingAccess: boolean;
  isSavingAccess: boolean;
  canManageAccess: boolean;
  showInvitesTable: boolean;
  onNewInvitation: () => void;
  onViewInvite: (row: InviteTableRow) => void;
  invitesIsLoading: boolean;
  isPreAccess: boolean;
  projectUuid?: string;
  onChange: ({ filtering, paging, sorting }: CursorChangeProps) => Promise<ResultType>;
}

export const useAccessTab = (): UseAccessTabReturnData => {
  const navigate = useNavigate();

  const { projectUuid } = useParams<"projectUuid">();

  const { currentOrganisationUuid, currentUserType } = useAuth();
  // isPreAccess is the READ_PROJECT check searchProjectOrganisations asserts, so a user without it - one following
  // a notification about access they no longer have - gets a message instead of loads that would all fail.
  const { hasProjectPermission, isPreAccess, projectDetails, shouldRefreshProjectDetails } = useContext(ProjectContext);

  const [invitesIsLoading, setInvitesIsLoading] = useState(true);
  const [accessIsLoading, setAccessIsLoading] = useState(true);
  const [accessRows, setAccessRows] = useState<AccessTableRow[]>([]);
  const [hasAccessError, setHasAccessError] = useState(false);
  const [isEditingAccess, setIsEditingAccess] = useState(false);
  const [selectedResponsibleOwnerUuid, setSelectedResponsibleOwnerUuid] = useState<string>();
  const [isSavingAccess, setIsSavingAccess] = useState(false);
  const [showAssignResponsibleOwnerModal, setShowAssignResponsibleOwnerModal] = useState(false);
  const [currentOrganisationRole, setCurrentOrganisationRole] = useState<string>();

  // Granted only where is_responsible_owner is true, so this is also the RO-only gate (REQ-018)
  // for both the New-invitation action and the per-row edit control.
  const canManageAccess = hasProjectPermission(ProjectPermissionConstants.MANAGE_PROJECT_ORGANISATION_INVITATION);

  const responsibleOwnerUuid = projectDetails?.responsibleOwner?.uuid ?? undefined;

  // Guests see only the Access table. Hidden until the role lands, so it never flashes up for one.
  const showInvitesTable = ProjectAccessConstants.isFullParticipantRole(currentOrganisationRole);

  // Read on its own rather than from accessRows, which stop at one page.
  useEffect(() => {
    let cancelled = false;

    const loadCurrentOrganisationRole = async (): Promise<void> => {
      setCurrentOrganisationRole(undefined);
      if (isPreAccess || !projectUuid || !currentOrganisationUuid) return;

      const response = await searchProjectOrganisations({
        projectUuid,
        filter: { results: { organisationUuid: { operator: "eq", value: currentOrganisationUuid } } },
        paging: { limit: 1, afterCursor: null, beforeCursor: null },
      });

      if (cancelled) return;

      if (response.status !== Status.Success || !response.data?.results) {
        Toast.error({ message: "Couldn't check your organisation's access to this project - please try again later." });
        return;
      }

      setCurrentOrganisationRole(
        response.data.results.find((result) => result.organisationUuid === currentOrganisationUuid)?.role
      );
    };

    loadCurrentOrganisationRole();

    return () => {
      cancelled = true;
    };
  }, [isPreAccess, projectUuid, currentOrganisationUuid]);

  const loadAccessRows = useCallback(async () => {
    if (isPreAccess || !projectUuid) return;

    setAccessIsLoading(true);
    setHasAccessError(false);

    // A project carries a handful of organisations, so this is read whole and the table runs
    // client-side; the api already filters to active, non-expired links. The page size is the
    // resolver's cap, not a choice - see ACCESS_ROWS_PAGE_SIZE.
    const response = await searchProjectOrganisations({
      projectUuid,
      paging: { limit: ProjectAccessConstants.ACCESS_ROWS_PAGE_SIZE, afterCursor: null, beforeCursor: null },
    });

    // Keyed off the status rather than the presence of errors - the rows already on screen are left
    // alone, so an error the grid cannot show still reaches the user as a toast.
    if (response.status !== Status.Success || !response.data) {
      setHasAccessError(true);
      const [error] = response.errors ?? [];
      Toast.error({
        message: error
          ? getErrorMessageFromCode(error.code, error.parameters ?? { property: error.property })
          : ACCESS_ROWS_ERROR_MESSAGE,
      });
      setAccessIsLoading(false);
      return;
    }

    setAccessRows(
      response.data.results.map((result) => ({
        organisationUuid: result.organisationUuid,
        organisationName: result.displayName ?? "",
        organisationType: OrganisationTypeLabels[result.organisationType] ?? "",
        role: result.role,
        // searchProjectOrganisations carries no responsible-owner flag - displayedAccessRows sets it
        // from the pending selection, which is itself seeded from projectDetails.
        isResponsibleOwner: false,
        canBeResponsibleOwner: ProjectAccessConstants.RESPONSIBLE_OWNER_ELIGIBLE_ROLES.includes(result.role),
      }))
    );

    // The grid has no paging control, so anything past the first page is unreachable.
    if (response.data.paging.hasNextPage) {
      Toast.error({ message: "Not all organisations could be shown for this project." });
    }

    setAccessIsLoading(false);
  }, [isPreAccess, projectUuid]);

  useEffect(() => {
    loadAccessRows();
  }, [loadAccessRows]);

  const onNewInvitation = (): void => navigate("invite");

  const navigateToInvite = useCallback(
    (invitationUuid: string): void => {
      if (!projectUuid) return;

      navigate(
        `${getProjectAccessDetailsRoute(invitationUuid, currentUserType, projectUuid)}?${
          NavigationSearchParams.projectAccessTab
        }=true`
      );
    },
    [projectUuid, currentUserType]
  );

  const onViewInvite = (row: InviteTableRow): void => navigateToInvite(row.invitationUuid);

  // projectDetails resolves after the first render, so the seed has to be reapplied when it lands.
  // Skipped mid-edit so a background refresh cannot pull the tick out from under the user.
  useEffect(() => {
    if (!isEditingAccess) setSelectedResponsibleOwnerUuid(responsibleOwnerUuid);
  }, [responsibleOwnerUuid, isEditingAccess]);

  // The ticks follow the pending selection, not the server, so Cancel can restore without a refetch.
  const displayedAccessRows = useMemo(
    () =>
      accessRows.map((row) => ({
        ...row,
        isResponsibleOwner: row.organisationUuid === selectedResponsibleOwnerUuid,
      })),
    [accessRows, selectedResponsibleOwnerUuid]
  );

  // Single-select - the tick moves off the previous holder, and re-clicking it is a no-op, so the
  // project always has exactly one target to send.
  const onResponsibleOwnerChange = (organisationUuid: string): void =>
    setSelectedResponsibleOwnerUuid(organisationUuid);

  const onEditAccess = (): void => setIsEditingAccess(true);

  const onCancelAccessEdit = (): void => {
    setSelectedResponsibleOwnerUuid(responsibleOwnerUuid);
    setIsEditingAccess(false);
  };

  // Assigning the owner takes the role off whoever holds it, so Save defers to the confirm (REQ-026).
  const onSaveAccessEdit = (): void => {
    if (selectedResponsibleOwnerUuid === responsibleOwnerUuid) {
      setIsEditingAccess(false);
      return;
    }

    setShowAssignResponsibleOwnerModal(true);
  };

  // Stays in edit mode, so the selection survives for another Save or an explicit Cancel.
  const onCancelAssignResponsibleOwner = (): void => setShowAssignResponsibleOwnerModal(false);

  const onConfirmAssignResponsibleOwner = useIsLoadingWrapper(async (): Promise<void> => {
    // responsibleOwner carries the row version the change is guarded by, so without it there is
    // nothing to send.
    if (!projectUuid || !selectedResponsibleOwnerUuid || !projectDetails?.responsibleOwner) {
      setShowAssignResponsibleOwnerModal(false);
      Toast.error({ message: "We could not read the project's current responsible owner. Please try again." });
      return;
    }

    const response = await changeProjectResponsibleOwner({
      projectUuid,
      targetOrganisationUuid: selectedResponsibleOwnerUuid,
      rowVersion: projectDetails.responsibleOwner.rowVersion,
    });

    setShowAssignResponsibleOwnerModal(false);

    // Stay in edit mode on failure so the selection survives for a retry or an explicit Cancel.
    if (response.status !== Status.Success) {
      // Only a 400 arrives with codes to read - the axios interceptor already toasts 403/404/409/500,
      // so a fallback message here would double up on it.
      const [error] = response.errors ?? [];
      if (error) {
        Toast.error({ message: getErrorMessageFromCode(error.code, error.parameters ?? { property: error.property }) });
      }
      // Any failure can mean the guard version is spent (ERROR_NO_ROWS_UPDATED, or a link changed
      // under us), and the request carries it, so refetch before the user retries.
      shouldRefreshProjectDetails();
      return;
    }

    Toast.success({ message: "You have successfully updated the project's responsible owner" });
    // Refreshes projectDetails.responsibleOwner - its row version is now spent, and the sync effect
    // above reseeds the selection from the new holder.
    shouldRefreshProjectDetails();
    setIsEditingAccess(false);
  }, setIsSavingAccess);

  const formatInvites = useCallback(
    (responseData: SearchProjectOrganisationInvitationsResponse | undefined): ResultData[] => {
      return (
        responseData?.results.map((d) => {
          const result = flattenObject({ ...d });
          // A request another organisation sent to this one, the responsible owner. It is shown by who is
          // asking, and opens where it can be accepted or rejected - as the settings page's Received list does.
          const isReceivedRequest =
            d.type === ProjectAccessConstants.TYPE_REQUEST && d.targetOrganisation.uuid === currentOrganisationUuid;
          const counterpartType = isReceivedRequest ? d.sourceOrganisationType : d.organisationType;
          const counterpart = isReceivedRequest ? d.sourceOrganisation : d.targetOrganisation;

          // Keys match ProjectInvitesTable's column definitions, which is what the grid renders in
          // server-side mode.
          result.organisationType = counterpartType ? (OrganisationTypeLabels[counterpartType] ?? "") : "";
          result.organisationName = counterpart.displayName ?? "";
          result.inviteType = ProjectAccessConstants.getInvitationTypeLabel(d.type);
          result.access = d.role;
          result.dateSent = d.sentDate;
          result.createdBy = d.sentByUser?.fullName ?? "";
          // BR-1092: a received item still awaiting a response reads as Pending, as on the settings page's
          // Received list; what this organisation sent keeps Sent.
          result.status =
            isReceivedRequest && d.calculatedStatus === ProjectAccessConstants.STATUS_SENT
              ? ProjectAccessConstants.STATUS_PENDING
              : d.calculatedStatus;
          result.viewLink = {
            action: () =>
              isReceivedRequest
                ? navigate(
                    `${getProjectAccessInviteAcceptRoute(d.uuid, currentUserType)}?${
                      NavigationSearchParams.projectAccessTab
                    }=true`
                  )
                : navigateToInvite(d.uuid),
            icon: createElement(EyeBlueIcon, { width: 20, height: 20, strokeWidth: 1.5 }),
          } as DataGridIconCellFormatterData;

          return result;
        }) || []
      );
    },
    [currentOrganisationUuid, currentUserType, navigate, navigateToInvite]
  );

  const onChange = async ({ filtering, paging, sorting }: CursorChangeProps): Promise<ResultType> => {
    const organisationNameFilter = filtering.find((f) => f.key === "organisationName");
    const filterCriteria = dataGridMapFilterCriteria(
      filtering
        .filter((f) => f.key !== "organisationName")
        .map((f) => ({ ...f, key: INVITE_COLUMN_TO_FILTER_KEY[f.key] ?? f.key }))
    );
    // The organisation name each row displays, matched against whichever side of the invitation that is.
    const displayedOrganisationName = organisationNameFilter
      ? { displayName: { operator: organisationNameFilter.operator, value: organisationNameFilter.value as string } }
      : undefined;

    filterCriteria.project = {
      uuid: {
        operator: "eq",
        value: projectUuid,
      },
    };

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const organisationUuid = currentOrganisationUuid!;

    // Invitations and requests this organisation sent (shown by the invited organisation), and access
    // requests sent to it (shown by the requester).
    const filterGroups: FilterGroups<SearchProjectOrganisationInvitationsRequest["filter"]>["filterGroups"] = [
      {
        operator: "or",
        filterGroups: [
          {
            operator: "and",
            filter: {
              results: {
                sourceOrganisation: { uuid: { operator: "eq", value: organisationUuid } },
                ...(displayedOrganisationName && { targetOrganisation: displayedOrganisationName }),
              },
            },
          },
          {
            operator: "and",
            filter: {
              results: {
                targetOrganisation: { uuid: { operator: "eq", value: organisationUuid } },
                type: { operator: "eq", value: ProjectAccessConstants.TYPE_REQUEST },
                ...(displayedOrganisationName && { sourceOrganisation: displayedOrganisationName }),
              },
            },
          },
        ],
      },
    ];

    let data: ResultType = {
      resultData: [],
      paging: {
        pageSize: 0,
        totalCount: 0,
        startCursor: "",
        endCursor: "",
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };

    await SearchProjectOrganisationInvitations({
      paging: {
        limit: paging.pageSize,
        beforeCursor: paging.beforeCursor || "",
        afterCursor: paging.afterCursor || "",
      },
      // A column with no server-side equivalent is dropped rather than sent as an unknown key.
      sort: sorting
        .filter((s) => !!INVITE_COLUMN_TO_SORT_KEY[s.key])
        .map((s) => ({
          key: INVITE_COLUMN_TO_SORT_KEY[s.key],
          direction: s.direction,
        })),
      filter: { results: filterCriteria },
      filterGroups,
    })
      .then((response) => {
        data = {
          resultData: formatInvites(response.data),
          paging: {
            startCursor: response.data?.paging?.startCursor || "",
            endCursor: response.data?.paging?.endCursor || "",
            pageSize: paging.pageSize || 10,
            totalCount: response.data?.paging?.total || 0,
            hasNextPage: response.data?.paging?.hasNextPage || false,
            hasPreviousPage: response.data?.paging?.hasPreviousPage || false,
          },
        };
      })
      .finally(() => {
        setInvitesIsLoading(false);
      });

    return data;
  };

  return {
    accessRows: displayedAccessRows,
    accessIsLoading,
    hasAccessError,
    onResponsibleOwnerChange,
    onEditAccess,
    onSaveAccessEdit,
    onCancelAccessEdit,
    showAssignResponsibleOwnerModal,
    newResponsibleOwnerName: accessRows.find((row) => row.organisationUuid === selectedResponsibleOwnerUuid)
      ?.organisationName,
    currentResponsibleOwnerName: projectDetails?.responsibleOwner?.displayName ?? undefined,
    onConfirmAssignResponsibleOwner,
    onCancelAssignResponsibleOwner,
    isEditingAccess,
    isSavingAccess,
    canManageAccess,
    showInvitesTable,
    onNewInvitation,
    onViewInvite,
    invitesIsLoading,
    isPreAccess,
    projectUuid,
    onChange,
  };
};
